import { Injectable } from '@angular/core';
import { Subject, fromEvent, Observable, from, of } from 'rxjs';
import { filter, debounceTime, map, tap } from 'rxjs/operators';

import { hasIntersectionObserver } from 'shared/utils/feature-detection';

import { LazyLoadConfig } from './lazy-load.config';


@Injectable()
export class LazyLoadObserverService {

  private readonly intersectionObserver: IntersectionObserver | undefined;
  private readonly eventObservable$: Observable<HTMLElement>;

  constructor(config: LazyLoadConfig) {
    if (hasIntersectionObserver()) {
      const loadingSubject$: Subject<HTMLElement> = new Subject();
      this.eventObservable$ = from(loadingSubject$);

      this.intersectionObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (!entry.isIntersecting) {
            return false;
          }
          loadingSubject$.next(entry.target as HTMLElement);
          return true;
        }, {
          threshold: config.threshold,
          rootMargin: config.rootMargin,
          root: config.root
        });
      });
    } else {
      this.eventObservable$ = fromEvent(window, 'scroll').pipe(
        debounceTime(50),
        map((ev) => ev.target as HTMLElement)
      );
    }
  }

  public observeElement(htmlElement: HTMLElement): Observable<HTMLElement> {
    if (isDefined(this.intersectionObserver)) {
      this.intersectionObserver.observe(htmlElement);
      return this.eventObservable$.pipe(
        filter((observedHTMLElement: HTMLElement) => observedHTMLElement === htmlElement),
        tap(() => this.unobserveElement(htmlElement))
      );
    }
    if (this.isVisible(htmlElement)) {
      return of(htmlElement);
    }
    return this.eventObservable$.pipe(
      filter(() => this.isVisible(htmlElement))
    );
  }

  public unobserveElement(htmlElement: HTMLElement) {
    if (isDefined(this.intersectionObserver)) {
      this.intersectionObserver.unobserve(htmlElement);
    }
  }

  private isVisible(htmlElement: HTMLElement) {
    const scrollPosition = this.getScrollPosition();
    const htmlElementOffset = htmlElement.offsetTop;
    return htmlElementOffset <= scrollPosition;
  }

  private getScrollPosition() {
    // Getting screen size and scroll position for IE
    return (window.scrollY || window.pageYOffset)
      + (document.documentElement.clientHeight || document.body.clientHeight);
  }

}
